import React, { FC, useState, useRef, useEffect } from "react";
import CartDropdown from "./CartDropdown";
import { useNavigate, useLocation } from "react-router-dom";
import GradientLogo from "../../assets/logos/GradientLogo.svg";
import { useAppDispatch, useAppSelector } from "store/hooks/hooks";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import AnimatedMenuButton, { AnimatedSingleDot } from "./AnimatedMenuButton";
import { ChevronRight, ExternalLink, Grid3X3, Package, X } from "lucide-react";
import { Input } from "components/UI/Input";
import { useTypingPlaceholder } from "hooks/useTypingPlaceholder";
import LucideIcon from "utils/LucideIcon";
import {
  fetchCategories,
  selectAllCategories,
  selectIsCategoriesLoaded,
} from "store/slices/categoriesSlice";
import { AppDispatch } from "store/Store";
import { ICategory, ISubCategory } from "store/types/categoriesTypes";
import { IHeader, IMenuItem } from "Interfaces/interface.nav";
import { selectIsAuthenticated, selectUser } from "store/slices/authSlice";
import ToTitleCase from "utils/ToTitleCase";
import Tippy from "@tippyjs/react";
import { environment } from "@/environments/environment";
import PromptVisualization from "./PromptVisualization";
import { selectLiveEvents } from "@/store/slices/eventsSlice";

const getResponsiveWidth = (
  isMenuOpen: boolean,
  isCategoryMenuOpen: boolean = false
): string => {
  if (isCategoryMenuOpen) return "100%";

  if (typeof window === "undefined") return isMenuOpen ? "90%" : "65%";

  const width = window.innerWidth;

  if (width < 640) {
    return "95%";
  } else if (width < 1024) {
    return isMenuOpen ? "80%" : "60%";
  } else {
    return isMenuOpen ? "70%" : "45%";
  }
};

const Header: FC<IHeader> = () => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const inputRef = useRef<HTMLInputElement>(null);
  const user = useAppSelector(selectUser);
  const categoriesData = useAppSelector(selectAllCategories);
  const isCategoriesLoaded = useAppSelector(selectIsCategoriesLoaded);
  const [selectedCategory, setSelectedCategory] = useState<ICategory | null>(
    null
  );
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);
  const liveEvents = useAppSelector(selectLiveEvents);
  const dispatch: AppDispatch = useAppDispatch();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isCategoriesLoaded && categoriesData.length === 0) {
      dispatch(fetchCategories());
    }
  }, [dispatch, categoriesData.length, isCategoriesLoaded]);

  const isUserInfoIncomplete =
    !user?.name?.trim() ||
    !user?.company?.trim() ||
    !user?.phoneNumber?.trim() ||
    !user?.emailVerified ||
    !user?.phoneNumberVerified;

  const [showSearchForm, setShowSearchForm] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isProductsPage = location.pathname === "/products";
  const isCuratedKitsPage = location.pathname === "/curated-kits";
  const isToolPage = location.pathname === "/catalog-generation-page" || location.pathname.startsWith("/mockup-generator");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCategoryMenuOpen, setISCategoryMenuOpen] = useState(false);
  const dynamicPlaceholder = useTypingPlaceholder([
    `Search gifts for your employees 🤔`,
    "Try personalized mugs 🍺",
    "Explore trending combos 👕👖",
    "Find something unique to offer this festive season 💫🎇",
  ]);

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleSearchToggle = () => {
    setShowSearchForm(!showSearchForm);
  };

  const handleCategoryMenuToggle = () => {
    setISCategoryMenuOpen(!isCategoryMenuOpen);
    if (isCategoryMenuOpen) {
      setSelectedCategory(null);
    }
  };

  const handleCloseAll = () => {
    setIsMenuOpen(false);
    setISCategoryMenuOpen(false);
    setShowSearchForm(false);
  };

  useEffect(() => {
    if (isCategoryMenuOpen) {
      // Lock body scroll
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = "0px"; // Prevent layout shift
    } else {
      // Unlock body scroll
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [isCategoryMenuOpen]);

  const [scrollDir, setScrollDir] = useState("up");
  const [lastScrollY, setLastScrollY] = useState(0);

  const [responsiveWidth, setResponsiveWidth] = useState(() =>
    getResponsiveWidth(isMenuOpen, isCategoryMenuOpen)
  );

  const isMenuOpenRef = useRef(isMenuOpen);
  const isCategoryMenuOpenRef = useRef(isCategoryMenuOpen);

  useEffect(() => {
    isMenuOpenRef.current = isMenuOpen;
    isCategoryMenuOpenRef.current = isCategoryMenuOpen;
    setResponsiveWidth(getResponsiveWidth(isMenuOpen, isCategoryMenuOpen));
  }, [isMenuOpen, isCategoryMenuOpen]);

  useEffect(() => {
    const handleResize = () => {
      setResponsiveWidth(
        getResponsiveWidth(isMenuOpenRef.current, isCategoryMenuOpenRef.current)
      );
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Scroll direction detection
  useEffect(() => {
    const updateScrollDir = () => {
      const scrollY = window.scrollY;
      const direction = scrollY > lastScrollY ? "down" : "up";

      if (
        direction !== scrollDir &&
        (scrollY - lastScrollY > 10 || scrollY - lastScrollY < -10)
      ) {
        setScrollDir(direction);
      }
      setLastScrollY(scrollY > 0 ? scrollY : 0);
    };

    window.addEventListener("scroll", updateScrollDir);
    return () => window.removeEventListener("scroll", updateScrollDir);
  }, [scrollDir, lastScrollY]);

  useEffect(() => {
    setShowSearchForm(false);
    setIsMenuOpen(false);
  }, [scrollDir]);

  const MenuData: IMenuItem[] = [
    {
      id: "Explore",
      name: "Explore",
      href: "/",
      isHeading: true,
      type: "#",
      children: [
        {
          id: "products",
          name: "Products",
          href: "/products",
          type: "headerMenu",
        },
        {
          id: "curated-kits",
          name: "Curated Kits",
          href: "/curated-kits",
          type: "headerMenu",
        },
        {
          id: "about",
          name: "About Us",
          href: "/about-us",
          type: "headerMenu",
        },
        {
          id: "official-site",
          name: `Visit Corporate Site`,
          href: `${environment.OFFICIAL_SITE_URL}`,
          type: "headerMenu",
          target: "_blanck",
        },
      ],
    },

    {
      id: "Support",
      name: "Support",
      href: "#",
      isHeading: true,
      children: [
        {
          id: "Contact Us",
          name: "Contact Us",
          href: "/contact-us",
        },
      ],
    },
    {
      id: "Actions",
      name: "Actions",
      href: "#",
      isHeading: true,
      children: [
        {
          id: "Sign Up",
          name: "Sign Up",
          href: "/signup",
        },
        {
          id: "Sign In",
          name: "Sign In",
          href: "/signin",
        },
        {
          id: "My Account",
          name: "My Account",
          href: "/profile",
        },
      ],
    },
    {
      id: "Other Solutions",
      name: "Other Solutions",
      isHeading: true,
      href: "/services",
      type: "headerMenu",
      children: [
        {
          id: "Brandstore",
          name: "BrandStore",
          href: "https://360brandstore.com",
          target: "_blanck",
        },
      ],
    },
  ];

  const handleOnSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const value = inputRef.current?.value.trim();
    if (value) {
      navigate(`/products/?searchQuery=${value}`);
    }
  };

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const value = inputRef.current?.value.trim();
      if (value) {
        navigate(`/products/?searchQuery=${value}`);
        setShowSearchForm(false);
      }
    }
  };

  const handleNavigateToProfile = () => {
    handleCloseAll();
    navigate("/profile");
  };

  const renderSearchDropdown = () => {
    return (
      <AnimatePresence>
        {showSearchForm && (
          <>
            <div
              className="fixed inset-0 z-[30]"
              onClick={handleSearchToggle}
            />

            {/* Search Dropdown */}
            <div className="flex justify-center items-center">
              <motion.div
                initial={{ opacity: 0, y: -10, width: responsiveWidth }}
                animate={{
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.2, ease: "easeOut" as const },
                }}
                exit={{
                  opacity: 0,
                  y: -10,
                  transition: { duration: 0.15, ease: "easeIn" as const },
                }}
                className="fixed top-[73px] border-t-0 rounded-t-none mx-auto bg-white dark:bg-neutral-900 border border-slate-300 dark:border-slate-700 rounded-2xl shadow-2xl z-[70]"
              >
                <div className="p-4">
                  <form
                    onSubmit={(e) => handleOnSubmit(e)}
                    className="space-y-3"
                  >
                    <div className="bg-slate-50 dark:bg-slate-800 flex items-center space-x-2 px-4 py-3 rounded-lg truncate">
                      <Input
                        ref={inputRef}
                        type="text"
                        placeholder={dynamicPlaceholder}
                        onKeyDown={(e) => handleSearch(e)}
                        className="text-xs md:text-sm truncate"
                        maxLength={255}
                      />
                      <button className="absolute right-10 hidden md:block bg-white ">
                        <LucideIcon name="Search" />
                      </button>
                    </div>
                  </form>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    );
  };

  const renderContent = () => {
    return (
      <div className="h-16 flex items-center justify-between px-4">
        <Link
          to="/"
          className="flex items-center justify-self-start z-10"
          onClick={handleCloseAll}
        >
          <img alt="360GiftHub" src={GradientLogo} className="w-12 h-12" />
        </Link>

        <div className="flex items-center justify-center gap-1 sm:gap-3 md:ml-10">
          {!isMenuOpen && !showSearchForm && !isCategoryMenuOpen && (
            <button onClick={handleCategoryMenuToggle}>
              <LucideIcon name="SlidersHorizontal" size="lg" className="mt-1" />
            </button>
          )}
          {!showSearchForm && !isCategoryMenuOpen && (
            <AnimatedMenuButton
              isOpen={isMenuOpen}
              onClick={handleMenuToggle}
            />
          )}

          {!isMenuOpen && !showSearchForm && !isCategoryMenuOpen && (
            <button onClick={handleSearchToggle}>
              <LucideIcon name="Search" size="md" className="mt-1" />
            </button>
          )}

          {!isMenuOpen && showSearchForm && (
            <AnimatedSingleDot onClick={handleSearchToggle} />
          )}
          {!isMenuOpen && isCategoryMenuOpen && (
            <AnimatedSingleDot onClick={handleCategoryMenuToggle} />
          )}
        </div>

        <div className="flex items-center justify-end z-10 max-w-[100px] dark:text-slate-100">
          {isAuthenticated ? (
            <Tippy
              content={
                <span className="text-xs font-inter">
                  Complete your profile.
                </span>
              }
              appendTo={() => document.body}
              zIndex={9999}
              interactive={true}
              theme="gradient"
              disabled={!isUserInfoIncomplete}
            >
              <div className="relative">
                <LucideIcon
                  name="User"
                  className="cursor-pointer"
                  onClick={handleNavigateToProfile}
                />
                {isUserInfoIncomplete && (
                  <span className="absolute -top-1 left-3  w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white dark:border-slate-900" />
                )}
              </div>
            </Tippy>
          ) : (
            <Link
              to="/signin"
              className="text-indigo-400 hover:text-indigo-700 text-sm sm:text-base whitespace-nowrap"
            >
              Sign In
            </Link>
          )}
          <div onClick={handleCloseAll}>
            <CartDropdown />
          </div>
        </div>
      </div>
    );
  };

  const renderDropdownMenu = () => {
    return (
      <AnimatePresence>
        {isMenuOpen && (
          <>
            <div className="fixed inset-0 z-30" onClick={handleMenuToggle} />

            <div className="flex justify-center">
              <motion.div
                initial={{ opacity: 0, scale: 1, width: responsiveWidth }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  scaleX: 1.02,
                  width: responsiveWidth,
                  transition: { duration: 0.2, ease: "easeOut" as const },
                }}
                exit={{
                  opacity: 0,
                  scale: 0.95,
                  transition: { duration: 0.1, ease: "easeIn" as const },
                }}
                style={{
                  transformOrigin: "top",
                }}
                className="fixed top-[73px] bg-white dark:bg-neutral-900 border border-slate-300 dark:border-slate-700 rounded-b-2xl shadow-2xl z-[50] border-t-0 rounded-t-none overflow-hidden"
              >
                <div className="w-full max-w-7xl mx-auto px-8 py-8 ">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    {MenuData.map((item, index) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, y: -20 }}
                        animate={{
                          opacity: 1,
                          y: 0,
                          transition: { delay: index * 0.1, duration: 0.3 },
                        }}
                        className="space-y-2"
                      >
                        <div className="font-semibold text-indigo-900 dark:text-slate-100 text-md">
                          {item.name}
                        </div>

                        {item.children && (
                          <div className="space-y-1">
                            {item.children
                              .filter((child) => {
                                if (item.id === "Actions") {
                                  if (isAuthenticated) {
                                    return child.name === "My Account";
                                  } else {
                                    return (
                                      child.name === "Sign Up" ||
                                      child.name === "Sign In"
                                    );
                                  }
                                }

                                return true;
                              })
                              .map((child) => (
                                <Link
                                  key={child.id}
                                  to={child.href}
                                  target={child.target || "_self"}
                                  onClick={handleMenuToggle}
                                  className="block text-black dark:text-slate-400 hover:text-indigo-500 dark:hover:text-blue-400 text-sm transition-colors"
                                >
                                  <div className="flex items-center space-x-1 ">
                                    <span>{child.name}</span>
                                    {child.id === "official-site" && (
                                      <ExternalLink className="w-4 h-4 " />
                                    )}
                                  </div>
                                </Link>
                              ))}
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>

                <div
                  className="absolute top-6 mt-[0.45rem] right-6 md:right-6 lg:right-8 cursor-pointer rounded-full hover:bg-slate-100 p-1"
                  onClick={handleMenuToggle}
                >
                  <X className="w-6 h-6" />
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    );
  };

  const renderCategoryDropdown = () => {
    return (
      <AnimatePresence>
        {isCategoryMenuOpen && (
          <>
            <div
              className="fixed inset-0 z-30"
              onClick={handleCategoryMenuToggle}
            />

            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{
                opacity: 1,
                y: 0,
                transition: { duration: 0.2, ease: "easeOut" as const },
              }}
              exit={{
                opacity: 0,
                y: -10,
                transition: { duration: 0.15, ease: "easeIn" as const },
              }}
              className="fixed top-[73px] left-0 right-0 bg-white dark:bg-neutral-900 border-b border-slate-300 dark:border-slate-700 shadow-2xl z-[50]"
              style={{
                height: "calc(100vh - 73px)",
              }}
              onWheel={(e) => e.stopPropagation()}
              onTouchMove={(e) => {
                const target = e.target as HTMLElement;
                const scrollableParent = target.closest(".overflow-y-auto");
                if (!scrollableParent) {
                  e.preventDefault();
                }
              }}
            >
              <div className={`h-full ${isMobile ? "flex" : "flex"}`}>
                {/* Categories Sidebar */}
                <div
                  className={`${isMobile ? "w-2/5 min-w-[140px] border-r" : "w-80 border-r"
                    } border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-neutral-800 flex flex-col`}
                >
                  <div
                    className={`${isMobile ? "px-3 py-3" : "p-4"
                      } border-b border-slate-200 dark:border-slate-700 flex-shrink-0`}
                  >
                    <h3
                      className={`font-semibold text-slate-900 dark:text-slate-100 ${isMobile ? "text-sm" : "text-lg"
                        }`}
                    >
                      Categories
                    </h3>
                  </div>

                  <div className="flex-1 overflow-y-auto">
                    {categoriesData.map(
                      (category: ICategory, index: number) => (
                        <motion.div
                          key={category.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{
                            opacity: 1,
                            x: 0,
                            transition: {
                              delay: index * 0.05,
                              duration: 0.2,
                            },
                          }}
                          className={`${isMobile ? "p-3" : "p-4"
                            } border-b border-slate-200 dark:border-slate-700 cursor-pointer transition-colors hover:bg-white dark:hover:bg-neutral-700 ${selectedCategory?.id === category.id
                              ? "bg-white dark:bg-neutral-700 border-r-2 border-r-indigo-500"
                              : ""
                            }`}
                          onClick={() => setSelectedCategory(category)}
                          onMouseEnter={() =>
                            !isMobile && setSelectedCategory(category)
                          }
                        >
                          <div className="flex items-center justify-between">
                            <div className="min-w-0 flex-1">
                              <h4
                                className={`font-medium text-slate-900 dark:text-slate-100 mb-1 ${isMobile ? "text-sm leading-tight" : ""
                                  } truncate`}
                              >
                                <ToTitleCase text={category.name} />
                              </h4>
                              <p
                                className={`text-slate-500 dark:text-slate-400 ${isMobile ? "text-xs" : "text-sm"
                                  }`}
                              >
                                {category.sub_category?.length || 0}{" "}
                                {isMobile ? "sub" : "subcategories"}
                              </p>
                            </div>
                            {!isMobile && (
                              <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 ml-2" />
                            )}
                          </div>
                        </motion.div>
                      )
                    )}
                  </div>
                </div>

                {/* Subcategories Content */}
                <div className="flex-1 bg-white dark:bg-neutral-900 flex flex-col min-w-0">
                  {selectedCategory ? (
                    <>
                      <div
                        className={`${isMobile ? "p-3" : "p-4"
                          } border-b border-slate-200 dark:border-slate-700 flex-shrink-0`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="min-w-0 flex-1 ">
                            <h3
                              className={`font-semibold  text-slate-900 dark:text-slate-100 mb-1 ${isMobile ? "text-base leading-tight" : "text-xl"
                                } truncate`}
                            >
                              <ToTitleCase text={selectedCategory.name} />
                            </h3>
                            <p
                              className={`text-slate-600 dark:text-slate-400 ${isMobile ? "text-xs" : "text-sm"
                                }`}
                            >
                              {selectedCategory.sub_category?.length || 0}{" "}
                              subcategories available
                            </p>
                          </div>
                        </div>
                      </div>

                      <div
                        className={`flex-1 overflow-y-auto ${isMobile ? "p-3" : "p-6"
                          }`}
                      >
                        {selectedCategory.sub_category &&
                          selectedCategory.sub_category.length > 0 ? (
                          <div
                            className={`grid gap-3 ${isMobile
                              ? "grid-cols-1"
                              : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                              }`}
                          >
                            {selectedCategory.sub_category.map(
                              (subcategory: ISubCategory, index: number) => (
                                <motion.div
                                  key={subcategory.id}
                                  initial={{ opacity: 0, y: 20 }}
                                  animate={{
                                    opacity: 1,
                                    y: 0,
                                    transition: {
                                      delay: index * 0.02,
                                      duration: 0.2,
                                    },
                                  }}
                                >
                                  <Link
                                    to={`/products?subCategory=${subcategory.id}`}
                                    onClick={handleCategoryMenuToggle}
                                    className={`block ${isMobile ? "p-3" : "p-3"
                                      } rounded-lg border border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-600 hover:bg-slate-50 dark:hover:bg-neutral-800 transition-all duration-200 group`}
                                  >
                                    <h5
                                      className={`font-medium text-slate-900 dark:text-slate-100 group-hover:text-indigo-500 dark:group-hover:text-indigo-400 transition-colors ${isMobile ? "text-sm leading-tight" : ""
                                        }`}
                                    >
                                      <ToTitleCase text={subcategory.name} />
                                    </h5>
                                  </Link>
                                </motion.div>
                              )
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-full">
                            <div className="text-center px-4">
                              <div
                                className={`${isMobile ? "w-12 h-12" : "w-16 h-16"
                                  } bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4`}
                              >
                                <Package
                                  className={`${isMobile ? "w-6 h-6" : "w-8 h-8"
                                    } text-slate-400`}
                                />
                              </div>
                              <h4
                                className={`font-medium text-slate-900 dark:text-slate-100 mb-2 ${isMobile ? "text-base" : "text-lg"
                                  }`}
                              >
                                No Subcategories
                              </h4>
                              <p
                                className={`text-slate-500 dark:text-slate-400 ${isMobile ? "text-sm" : ""
                                  }`}
                              >
                                This category doesn't have any subcategories
                                yet.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center px-4">
                        <div
                          className={`${isMobile ? "w-16 h-16" : "w-20 h-20"
                            } bg-indigo-100 dark:bg-indigo-900/20 rounded-full flex items-center justify-center mx-auto mb-4`}
                        >
                          <Grid3X3
                            className={`${isMobile ? "w-8 h-8" : "w-10 h-10"
                              } text-indigo-500`}
                          />
                        </div>
                        <h4
                          className={`font-medium text-slate-900 dark:text-slate-100 mb-2 ${isMobile ? "text-base" : "text-xl"
                            }`}
                        >
                          Select a Category
                        </h4>
                        <p
                          className={`text-slate-500 dark:text-slate-400 max-w-sm ${isMobile ? "text-sm" : "text-sm"
                            }`}
                        >
                          {isMobile
                            ? "Tap a category to explore its subcategories"
                            : "Choose a category from the sidebar to explore its subcategories"}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <button
                className={`absolute top-4 right-3 ${isMobile ? "p-1.5" : "p-1"
                  } hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors z-10`}
                onClick={handleCategoryMenuToggle}
                aria-label="Close menu"
              >
                <X
                  className={`${isMobile ? "w-5 h-5" : "w-6 h-6"
                    } text-slate-600 dark:text-slate-400`}
                />
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    );
  };



  return (
    <>
      <motion.div
        initial={{
          y: -40,
          opacity: 0,
        }}
        animate={{
          y: 0,
          opacity: 1,
        }}
        transition={{
          delay: 1,
          duration: 0.5,
          ease: "easeOut" as const,
        }}
        className="flex justify-center items-center"
      >
        <motion.div
          className="fixed top-2 z-[40]"
          initial={{
            width: responsiveWidth,
            y: 0,
          }}
          animate={{
            width: responsiveWidth,
            scaleX: isMenuOpen || isCategoryMenuOpen ? 1.02 : 1,
            y: scrollDir === "down" && !isCategoryMenuOpen && !isMenuOpen && !showSearchForm ? -200 : 0,
            opacity: scrollDir === "down" && !isCategoryMenuOpen && !isMenuOpen && !showSearchForm ? 0 : 1,
          }}
          transition={{
            width: {
              duration: 0.6,
              ease: [0.68, -0.55, 0.265, 1.55],
              type: "spring",
              stiffness: 300,
              damping: 30,
            },
            y: {
              duration: 0.4,
              ease: "easeInOut" as const,
            },
            opacity: {
              duration: 0.4,
              ease: "easeInOut" as const,
            },
            scaleX: {
              duration: 0.6,
              ease: [0.68, -0.55, 0.265, 1.55],
              type: "spring",
              stiffness: 300,
              damping: 30,
            },
          }}
          style={{
            transformOrigin: "center",
          }}
        >
          <div className={`nc-Header bg-white/80 dark:bg-neutral-900 border border-slate-300 dark:border-slate-700 shadow-xl rounded-2xl ${(isMenuOpen || isCategoryMenuOpen) && "rounded-b-none border-b-0"
            } ${showSearchForm && "rounded-b-none border-b-0"} backdrop-blur-sm w-full`}>
            <div className="w-full max-w-7xl mx-auto px-2">{renderContent()}</div>
          </div>
          {!isProductsPage && !isCuratedKitsPage && !isToolPage && liveEvents.length > 0 && (
            <PromptVisualization
              isMenuOpen={isMenuOpen}
              isCategoryMenuOpen={isCategoryMenuOpen}
              showSearchForm={showSearchForm}
            />
          )}
        </motion.div>
      </motion.div>
      {renderCategoryDropdown()}
      {renderDropdownMenu()}
      {renderSearchDropdown()}
    </>
  );
};

export default Header;
